import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import useSWRImmutable from 'swr/immutable';
import type { ApiClassDetail } from '@engage/class-detail';
import { toClassDetail } from '@engage/class-detail/mappers';
import type { ClassAnalyticsProperties } from '@engage/classes';
import { getClassAnalyticsProperties } from '@engage/classes';
import { toCloudinaryUrl } from '@engage/cloudinary/toCloudinaryUrl';
import { useReduxState } from '@engage/redux';
import { track } from '@members/analytics';
import { useAria } from '@members/copy';
import { Aria } from '@members/copy/models.generated/Aria';
import { StreamFetchers, ClassFetchers } from '@members/data-fetch';
import PlayIcon from '@members/icons/Play';
import PauseIcon from '../icons/PauseIcon';
import { trackPausedClassPreview, trackPreviewedClass } from './events';

const PREVIEW_FADE_IN_DURATION = 1;
const CLASS_PREVIEW_LENGTH = 30;

export const VideoPreview: React.FC<
  React.PropsWithChildren<{
    currentStartTime: string;
    rideId: string;
    imageUrl: string;
    source: string;
    loop?: boolean;
    showPlayButton?: boolean;
    hoveringOnCard?: boolean;
    onReadyCallback?: () => void;
  }>
> = ({
  currentStartTime,
  rideId,
  imageUrl,
  source,
  loop = false,
  showPlayButton = false,
  hoveringOnCard = false,
  onReadyCallback = () => {},
}) => {
  const { data } = useSWRImmutable(
    ...StreamFetchers.CreateAkamaiStream({
      currentStartTime,
      rideId: rideId,
      contentType: 'preview',
    }),
  );
  const streamURL = data?.url + `#t=${PREVIEW_FADE_IN_DURATION}`;
  const posterSrc = toCloudinaryUrl(imageUrl, {
    width: 512,
  });

  const [videoElement, setVideoElement] = React.useState<HTMLVideoElement>();
  const [playing, setPlaying] = React.useState(true);
  const classInfo = useReduxState(getClassAnalyticsProperties, rideId ?? '');
  const classPreviewTitle = useAria(Aria.ClassPreview);
  const classPreviewVideoAriaLabel = useAria(Aria.ClassPreviewVideo);

  const { data: classDetailsData } = useSWRImmutable.apply(
    '',
    !classInfo && playing
      ? ClassFetchers.GetClassDetails({
          classId: rideId,
        })
      : [],
  );

  const handleVideo = React.useCallback((node: any) => {
    node && setVideoElement(node);
  }, []);

  const handleClick = () => {
    if (playing && videoElement) {
      videoElement.pause();
      setPlaying(false);
      // class preview videos are always 30 seconds long
      const previewPercentComplete =
        (videoElement.currentTime / CLASS_PREVIEW_LENGTH) * 100;
      track(
        trackPausedClassPreview(
          classInfo as ClassAnalyticsProperties,
          previewPercentComplete,
        ),
      );
    } else if (videoElement) {
      videoElement.play();
      setPlaying(true);
    }
  };

  useEffect(() => {
    if (!videoElement) return;
    // Handle the case where the video element may render and fetch data before we have a chance to attach an event listener
    if (videoElement?.readyState >= videoElement?.HAVE_FUTURE_DATA) {
      onReadyCallback();
    } else {
      videoElement?.addEventListener('canplay', onReadyCallback);
    }
    return () => videoElement?.removeEventListener('canplay', onReadyCallback);
  }, [videoElement]);

  // We only want to call the analytics endpoint if the user has watched
  // over 3 seconds of the preview
  React.useEffect(() => {
    let maybeTimeout: ReturnType<typeof setTimeout>;

    if (playing && videoElement && (classDetailsData || classInfo)) {
      maybeTimeout = setTimeout(() => {
        const previewPercentComplete =
          ((videoElement?.currentTime ?? 0) / CLASS_PREVIEW_LENGTH) * 100;
        let analyticsInfo;

        // The analytics info is either class details from the API if we're on
        // the homescreen, or classInfo from redux if we're anywhere else
        if (classDetailsData) {
          analyticsInfo = (toClassDetail(classDetailsData as ApiClassDetail)
            .class as unknown) as ClassAnalyticsProperties;
        } else {
          analyticsInfo = classInfo as ClassAnalyticsProperties;
        }

        track(trackPreviewedClass(source, analyticsInfo, previewPercentComplete));
      }, 3000);
    }
    // Clear timeout when the component ummounts (aka stops playing)
    return () => clearTimeout(maybeTimeout);
  }, [playing, videoElement, classDetailsData]);

  return data?.url ? (
    <>
      <Wrapper>
        <StyledVideo
          data-test-id="videoPreview"
          id="videoPreview"
          ref={handleVideo}
          src={streamURL}
          autoPlay={true}
          muted
          playsInline
          poster={posterSrc}
          loop={loop}
          title={classPreviewTitle}
          aria-label={classPreviewVideoAriaLabel}
        >
          <track kind="captions" src="" label="Captions" />
          <p>Your browser does not support HTML5 video.</p>
        </StyledVideo>
      </Wrapper>
      {showPlayButton && !playing ? (
        <PreviewButton
          data-test-id="previewButton"
          onClick={handleClick}
          playing={playing}
        >
          <StyledPlayIcon width={20} height={20} />
          PREVIEW
        </PreviewButton>
      ) : null}
      {showPlayButton && playing && hoveringOnCard ? (
        <PreviewButton data-test-id="pauseButton" onClick={handleClick} playing={playing}>
          <PauseIcon />
        </PreviewButton>
      ) : null}
    </>
  ) : null;
};

const StyledVideo = styled.video`
  width: 100%;
`;

const Wrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 5px;
  overflow: hidden;
  z-index: -1;
`;
const StyledPlayIcon = styled(PlayIcon)`
  margin-right: 6px;
`;
const FadeIn = keyframes`
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
`;

const PreviewButton = styled.button<{ playing?: boolean }>`
  animation: ${FadeIn} 250ms ease;
  background: rgba(0, 0, 0, 0.48);
  border-radius: 9999px;
  position: absolute;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 14px;
  padding: ${props => (props.playing ? '10px' : '12px')};
  margin: ${props => (props.playing ? '133px 256px' : '133px 222px')};
  z-index: 1;
`;
